import { optionalPositiveInteger, optionalTrimmedString } from '../../../lib/routeSearch';
import {
  defaultNetworksFilter,
  isNetworkSortProperty,
  NetworkSortDirection,
  NetworkStatus,
  type NetworksFilter,
  type NetworkSortProperty,
} from './model';

export interface NetworksRouteSearch {
  search?: string;
  statuses?: string;
  page?: number;
  size?: number;
  sortProperty?: NetworkSortProperty | null;
  sortDirection?: NetworkSortDirection;
}

export function networksRouteSearchSchema(search: Record<string, unknown>): NetworksRouteSearch {
  const searchTerm = optionalTrimmedString(search['search']);
  const statuses = typeof search['statuses'] === 'string' ? search['statuses'] : undefined;
  const page = optionalPositiveInteger(search['page']);
  const size = optionalPositiveInteger(search['size']);
  const rawSortProperty = optionalTrimmedString(search['sortProperty']);
  const sortProperty =
    rawSortProperty && isNetworkSortProperty(rawSortProperty) ? rawSortProperty : undefined;
  const sortDirection = Object.values(NetworkSortDirection).includes(
    search['sortDirection'] as NetworkSortDirection,
  )
    ? (search['sortDirection'] as NetworkSortDirection)
    : undefined;
  return {
    ...(searchTerm ? { search: searchTerm } : {}),
    ...(statuses !== undefined ? { statuses } : {}),
    ...(page ? { page } : {}),
    ...(size ? { size } : {}),
    ...(sortProperty ? { sortProperty } : {}),
    ...(search['sortProperty'] === null ? { sortProperty: null } : {}),
    ...(sortDirection ? { sortDirection } : {}),
  };
}

const networkStatuses: ReadonlySet<string> = new Set(Object.values(NetworkStatus));

export function parseNetworksRouteSearch(search: NetworksRouteSearch): NetworksFilter {
  const statuses =
    search.statuses === undefined
      ? [...defaultNetworksFilter.statuses]
      : search.statuses
          .split(',')
          .filter((status): status is NetworkStatus => networkStatuses.has(status));
  return {
    searchTerm: search.search ?? defaultNetworksFilter.searchTerm,
    statuses,
    page: search.page ? search.page - 1 : defaultNetworksFilter.page,
    size: search.size ?? defaultNetworksFilter.size,
    sortProperty:
      search.sortProperty === null
        ? null
        : (search.sortProperty ?? defaultNetworksFilter.sortProperty),
    sortDirection: search.sortDirection ?? defaultNetworksFilter.sortDirection,
  };
}

export function serializeNetworksRouteSearch(filter: NetworksFilter): NetworksRouteSearch {
  const statuses = filter.statuses.join(',');
  return {
    ...(filter.searchTerm.trim() ? { search: filter.searchTerm.trim() } : {}),
    ...(statuses !== defaultNetworksFilter.statuses.join(',') ? { statuses } : {}),
    ...(filter.page ? { page: filter.page + 1 } : {}),
    ...(filter.size !== defaultNetworksFilter.size ? { size: filter.size } : {}),
    ...(filter.sortProperty !== defaultNetworksFilter.sortProperty
      ? { sortProperty: filter.sortProperty }
      : {}),
    ...(filter.sortDirection !== defaultNetworksFilter.sortDirection
      ? { sortDirection: filter.sortDirection }
      : {}),
  };
}
